import React, { useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { reactDnDTreeView } from '@yola/ws-ui';
import TreeViewItemContainer from './tree-view-item-container.js';
import TreeViewItemDragPreview from './tree-view-item-drag-preview.js';
import TreeViewItemPlaceholder from './tree-view-item-placeholder.js';
import useTreeViewData from '../../hooks/use-tree-view-data';

const { Tree } = reactDnDTreeView;

const treeClasses = {
  root: 'ws-page-manager-tree-view-list--root',
  container: 'ws-page-manager-tree-view-list',
  listItem: 'ws-page-manager-tree-view-item-container',
  draggingSource: 'ws-page-manager-tree-view-item-container--dragging',
  dropTarget: 'ws-page-manager-tree-view-item-container--drop-target',
  placeholder: 'ws-page-manager-tree-view-item-placeholder',
};

const MAX_NESTING_LEVEL = 4;

const PageManagerTreeView = ({
  rootId,
  items,
  selectModeEnabled = false,
  selectedItemsMap = {},
  onItemSelect = Function.prototype,
  onChange = Function.prototype,
  onItemClick = Function.prototype,
  onStartItemDrag = Function.prototype,
  onEndItemDrag = Function.prototype,
}) => {
  const { isDragging, openNodeIds, itemsDepthMap, setIsDragging, setOpenNodeIds } = useTreeViewData(
    items,
    rootId
  );

  const handleDrop = useCallback(
    (newTree) => {
      onChange(newTree);
    },
    [onChange]
  );

  const handleChangeOpen = useCallback(
    (newOpenIds) => {
      setOpenNodeIds(newOpenIds);

      const updatedFlatTree = items.map((node) => {
        if (!node?.droppable) return node;

        return {
          ...node,
          collapsed: !newOpenIds.includes(node?.data.id),
        };
      });

      onChange(updatedFlatTree);
    },
    [setOpenNodeIds, items, onChange]
  );

  const handleItemClick = useCallback(
    (item) => {
      onItemClick(item);
    },
    [onItemClick]
  );

  const handleDragStart = useCallback(
    (node) => {
      setIsDragging(true);
      if (node.data) {
        onStartItemDrag(node.data);
      }
    },
    [onStartItemDrag, setIsDragging]
  );

  const handleDragEnd = useCallback(
    (node) => {
      setIsDragging(false);
      if (node.data) {
        onEndItemDrag(node.data);
      }
    },
    [onEndItemDrag, setIsDragging]
  );

  const canDrag = useCallback((node) => node.draggable, []);

  const canDrop = useCallback(
    (_, options) => {
      const { dragSource, dropTarget, dragSourceId, dropTargetId } = options;

      if (!dragSource || dragSourceId === undefined) {
        return false;
      }

      if (dropTargetId === rootId || dropTargetId == null) {
        return true;
      }

      if (!dropTarget?.droppable || dropTargetId === dragSourceId) {
        return false;
      }

      const dragDepthEntry = itemsDepthMap.get(dragSourceId);
      const dropTargetEntry = itemsDepthMap.get(dropTargetId);

      if (!dragDepthEntry) return false;
      if (!dropTargetEntry) return false;

      const dropDepth = dropTargetEntry?.depth + 1;
      const dragMaxChildDepth = dragDepthEntry.maxChildDepth;
      const totalDepth = dropDepth + dragMaxChildDepth;

      return totalDepth <= MAX_NESTING_LEVEL;
    },
    [itemsDepthMap, rootId]
  );

  const renderCallback = useCallback(
    (node, options) => (
      <TreeViewItemContainer
        {...options}
        node={node}
        selectModeEnabled={selectModeEnabled}
        selectedItemsMap={selectedItemsMap}
        onItemSelect={onItemSelect}
        onItemClick={handleItemClick}
      />
    ),
    [handleItemClick, selectModeEnabled, selectedItemsMap, onItemSelect]
  );

  const placeholderRenderCallback = useCallback(() => <TreeViewItemPlaceholder />, []);

  const dragPreviewRenderCallback = useCallback(
    (monitorProps) => <TreeViewItemDragPreview {...monitorProps} />,
    []
  );

  const classes = classNames('ws-page-manager-tree-view', {
    'ws-page-manager-tree-view--drag-mode': isDragging,
  });

  return (
    <div className={classes} data-drag-backend="multibackend">
      <Tree
        tree={items}
        rootId={rootId}
        classes={treeClasses}
        initialOpen={openNodeIds}
        sort={false}
        insertDroppableFirst={false}
        dropTargetOffset={5}
        onDrop={handleDrop}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        canDrop={canDrop}
        canDrag={canDrag}
        onChangeOpen={handleChangeOpen}
        render={renderCallback}
        placeholderRender={placeholderRenderCallback}
        dragPreviewRender={dragPreviewRenderCallback}
      />
    </div>
  );
};

PageManagerTreeView.propTypes = {
  rootId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func,
  onItemClick: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectModeEnabled: PropTypes.bool,
  selectedItemsMap: PropTypes.shape({}),
  onItemSelect: PropTypes.func,
  onStartItemDrag: PropTypes.func,
  onEndItemDrag: PropTypes.func,
};

export default PageManagerTreeView;
